import React, { useEffect, useMemo, useState } from "react";
import { ModalWrapper } from "./ModalWrapper";
import { POPUP_STYLES } from "../../constants/popupStyles";
import apiService from "../../services/api";
import { useCall } from "../../contexts/CallContext";
import { WhatsAppCallPermissionHint } from "../WhatsAppCallPermissionHint";
import { CallMethodSelector, CallMethod } from "../CallMethodSelector";
import { useWhatsAppCallPermission } from "../../hooks/useWhatsAppCallPermission";
import { displayPhoneNumber } from "../../utils/phoneUtils";

interface PhoneOption {
  label: string;
  // What is dialed - the number exactly as it was given to this modal.
  value: string;
  // What is shown. Callers hand over whatever their source held: patient_page has already
  // run the number through display_phone_number, get_patients_tasks has not. Formatting here
  // means every caller's picker reads the same way, and it is a no-op on a number that is
  // already local.
  display: string;
}

interface Props {
  patientId: number;
  patientName?: string;
  patientPhone?: string;
  parent1Phone?: string;
  parent2Phone?: string;
  token: string;
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}

export const CallPatientConfModal: React.FC<Props> = ({
  patientId,
  patientName,
  patientPhone,
  parent1Phone,
  parent2Phone,
  token,
  onClose,
  onSuccess,
  onError,
}) => {
  const phoneOptions = useMemo<PhoneOption[]>(() => {
    const options: PhoneOption[] = [];
    const seen = new Set<string>();
    const normalize = (phone: string) => phone.replace(/\D/g, "");
    const add = (label: string, phone: string | undefined | null) => {
      const p = phone?.trim();
      if (!p) return;
      const key = normalize(p);
      if (!seen.has(key)) {
        seen.add(key);
        options.push({ label, value: p, display: displayPhoneNumber(p) });
      }
    };
    add("Patient", patientPhone);
    add("Parent 1", parent1Phone);
    add("Parent 2", parent2Phone);
    return options;
  }, [patientPhone, parent1Phone, parent2Phone]);

  const [selectedPhone, setSelectedPhone] = useState(phoneOptions[0]?.value ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [callMethod, setCallMethod] = useState<CallMethod>("phone");
  const [methodTouched, setMethodTouched] = useState(false);
  const { startWhatsAppCall } = useCall();
  const { permission, setPermission, approved } = useWhatsAppCallPermission({
    token,
    patientId,
    phone: selectedPhone,
  });

  // Until the therapist picks a method themselves, follow the patient's stored
  // consent: an approved patient is best reached on WhatsApp, and defaulting to
  // it saves the therapist noticing the badge and switching every time. It
  // tracks the selected number, so changing to an unapproved one falls back to
  // phone rather than leaving a method that would fail.
  // Guarded on `permission` so a re-fetch (the therapist switching number) does
  // not flip the radio to phone and back while the lookup is in flight.
  useEffect(() => {
    if (!methodTouched && permission) setCallMethod(approved ? "whatsapp" : "phone");
  }, [approved, permission, methodTouched]);

  const handleMethodChange = (method: CallMethod) => {
    setMethodTouched(true);
    setCallMethod(method);
  };

  const handleCall = async () => {
    if (callMethod === "whatsapp") {
      void startWhatsAppCall({
        phone: selectedPhone,
        patientId,
        patientName: patientName || selectedPhone,
      });
      onClose();
      return;
    }

    setSubmitting(true);
    try {
      await apiService.callPatientConf({ token, patient_id: patientId, phone: selectedPhone, call_type: "both" });
      onSuccess();
      onClose();
    } catch (e: any) {
      onError(e.response?.data?.error || e.message || "Call failed");
      onClose();
    }
  };

  return (
    <ModalWrapper modalId="CallPatientConfModal"
      isOpen={true}
      onClose={onClose}
      title="Call Patient"
      isLoading={submitting}
      width="max-w-sm"
    >
      <div className="space-y-5">
        {patientName && (
          <p className="text-sm text-[#366A75] font-medium -mt-2">{patientName}</p>
        )}

        <CallMethodSelector value={callMethod} onChange={handleMethodChange} whatsappApproved={approved} />

        {/* Stored call-permission state for the number about to be dialed */}
        {callMethod === "whatsapp" && selectedPhone && (
          <WhatsAppCallPermissionHint
            token={token}
            patientId={patientId}
            phone={selectedPhone}
            permission={permission}
            onPermissionChange={setPermission}
          />
        )}

        <div>
          <label className="block text-sm font-medium text-[#366A75] mb-2">Phone Number</label>
          {phoneOptions.length === 0 ? (
            <p className="text-sm text-[#366A75]/60">No phone number available</p>
          ) : phoneOptions.length === 1 ? (
            <div className={`${POPUP_STYLES.input} text-sm text-[#366A75]`}>
              <span className="font-medium">{phoneOptions[0].label}:</span> {phoneOptions[0].display}
            </div>
          ) : (
            <div className="space-y-2">
              {phoneOptions.map((opt) => (
                <label key={opt.value} className="flex items-center gap-3 cursor-pointer px-4 py-2 rounded-lg bg-[#EBF5F5] hover:bg-[#D0E7E5] transition-colors">
                  <input
                    type="radio"
                    name="phone"
                    value={opt.value}
                    checked={selectedPhone === opt.value}
                    onChange={() => setSelectedPhone(opt.value)}
                    className="accent-[#366A75]"
                  />
                  <span className="text-sm text-[#366A75]">
                    <span className="font-medium">{opt.label}</span>
                    <span className="ml-2 opacity-70">{opt.display}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3 justify-end pt-1">
          <button onClick={onClose} className={POPUP_STYLES.button} disabled={submitting}>
            Cancel
          </button>
          <button
            onClick={handleCall}
            disabled={submitting || !selectedPhone}
            className={POPUP_STYLES.button}
          >
            {submitting ? "Calling…" : callMethod === "whatsapp" ? "WhatsApp Call" : "Call"}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};
